import {
  APICallState,
  CountryDropdown,
  FormField,
  PanelContainer,
  ProvinceDropdown,
  StateDropdown,
  useCountry,
  useIsDesktop,
  useProvince,
  useStateUS,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {yupResolver} from '@hookform/resolvers/yup';
import BusinessInfoIcon from 'assets/ProfileEmptyStateIcons/icon_business_info.svg';
import {Flex, View} from 'native-base';
import React, {useEffect, useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useWindowDimensions} from 'react-native';

import {getZipCode} from 'components/PersonalInfoModule/PersonalInfoModule';
import ProfileEmptyStateModule from 'components/ProfileEmptyState/ProfileEmptyStateModule';
import {useMaskPhoneNumber} from 'hooks/useMaskPhoneNumber';
import {useMaskZip} from 'hooks/useMaskZip';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {BusinessInfo} from 'models/Profile';
import {BusinessInfoModuleValidation} from 'validation/module/BusinessInfoValidation';

export interface BusinessInfoModuleProps {
  address?: string;
  address2?: string;
  phonenumber?: string;
  city?: string;
  country?: string;
  stateprovince?: string;
  zip?: string;
  profileInfoState: APICallState;
  handleSaveProfileInfo: (data: BusinessInfo) => void;
  isPanelUnlocked: boolean;
  onEdit: () => void;
  stopEditing: () => void;
  onOverlayAnimationFinish: () => void;
  isEditLoading?: boolean;
}

const TEST_ID_PREFIX = 'profile-business-info-module';

const BusinessInfoModule: React.FC<BusinessInfoModuleProps> = props => {
  const {
    address,
    address2,
    phonenumber,
    city,
    country,
    stateprovince,
    zip,
    profileInfoState,
    handleSaveProfileInfo,
    isPanelUnlocked,
    onEdit,
    stopEditing,
    onOverlayAnimationFinish,
    isEditLoading,
  } = props;
  const countryDisplay = useCountry(country ?? '');
  const stateDisplay = useStateUS(stateprovince ?? '');
  const provinceDisplay = useProvince(stateprovince ?? '');

  const {maskNumber, unmaskNumber} = useMaskPhoneNumber();

  const defaultValues = useMemo(
    () => ({
      address,
      address2,
      city,
      country: {display: countryDisplay, code: country ?? ''},
      phonenumber,
      state: stateDisplay
        ? {
            display: stateDisplay,
            code: stateprovince || '',
          }
        : undefined,
      province: provinceDisplay
        ? {
            display: provinceDisplay,
            code: stateprovince || '',
          }
        : undefined,
      zip,
    }),
    [
      address,
      address2,
      city,
      countryDisplay,
      country,
      phonenumber,
      stateDisplay,
      stateprovince,
      provinceDisplay,
      zip,
    ]
  );

  const {handleSubmit, control, formState, setValue, getValues, watch, reset} =
    useForm({
      defaultValues,
      resolver: yupResolver(BusinessInfoModuleValidation),
      mode: 'onBlur',
    });
  const isDesktop = useIsDesktop();
  const formValues = getValues();
  const watchCountry = watch('country');
  const watchState = watch('state');
  const watchProvince = watch('province');
  const isCanada = watchCountry.code === 'CAN';
  const isUSA = watchCountry.code === 'USA';
  const {errors} = formState;
  const countryValue = useCountry(country ?? '');
  const maskZip = useMaskZip({isCanada, isUSA});

  const {height} = useWindowDimensions();
  const dropdownMaxHeight = isDesktop ? `${height / 2}px` : `${height / 4}px`;

  // Reset the form if we are not editing - rerender for loading finished
  useEffect(() => {
    if (isPanelUnlocked) {
      return;
    }

    reset(defaultValues);
  }, [isPanelUnlocked, reset, defaultValues]);

  const any =
    address ||
    address2 ||
    countryValue ||
    phonenumber ||
    city ||
    stateprovince ||
    zip ||
    isPanelUnlocked;

  const {trackAdobeEvent} = useTrackEvent();

  const onEditPress = () => {
    onEdit();
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'edit business information',
      },
    });
  };

  const onCancelPress = () => {
    stopEditing();
    reset(defaultValues);
  };

  const onSavePress = () => {
    handleSubmit(data => {
      handleSaveProfileInfo({
        businessphone: unmaskNumber(data.phonenumber),
        businessaddress1: data.address,
        businessaddress2: data.address2,
        businesscity: data.city,
        businesszip: getZipCode({isUSA, zip: data.zip || ''}),
        businessstate: isUSA ? data.state?.code : data.province?.code,
        businesscountry: data.country.code,
      });
    })();
  };

  const emptyState = (
    <View>
      <ProfileEmptyStateModule
        ctaTitle="Add Business Information"
        body="You have no business information on file."
        emptyStateTitle="ADD BUSINESS INFORMATION"
        emptyStateIcon="briefcase"
        onCTAPress={onEditPress}
        svgIcon={BusinessInfoIcon}
      />
    </View>
  );

  if (!any && !isPanelUnlocked) {
    return emptyState;
  }

  return (
    <PanelContainer
      isLoading={isEditLoading}
      icon="briefcase"
      panelTitle="Business Information"
      contentMaxWidth="100%"
      showEdit={!isPanelUnlocked && !!any}
      onEdit={onEditPress}
      onCancel={onCancelPress}
      onSave={onSavePress}
      apiCallState={profileInfoState}
      hasFooter={isPanelUnlocked}
      onOverlayAnimationFinish={onOverlayAnimationFinish}>
      <View
        mt={2}
        flexDirection="column"
        alignItems={isDesktop ? 'flex-start' : null}>
        <View minW={isDesktop ? null : '100%'}>
          {!phonenumber && !isPanelUnlocked ? null : (
            <View mb={isDesktop ? '40px' : 4}>
              <Controller
                name="phonenumber"
                control={control}
                render={({field}) => (
                  <FormField
                    nativeID={`${TEST_ID_PREFIX}-business-phone`}
                    testID={`${TEST_ID_PREFIX}-business-phone`}
                    label="BUSINESS PHONE"
                    editable={isPanelUnlocked}
                    isInvalid={!!errors.phonenumber}
                    isInvalidLabel={errors?.phonenumber?.message}
                    value={field.value}
                    onChangeText={text => field.onChange(maskNumber(text))}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </View>
          )}
          {!country && !isPanelUnlocked ? null : (
            <View mb={isDesktop ? '40px' : '24px'}>
              <CountryDropdown
                maxHeight={dropdownMaxHeight}
                hasNone
                required={
                  formValues?.address || formValues?.city || formValues?.zip
                    ? true
                    : false
                }
                editable={isPanelUnlocked}
                onSelected={value => {
                  setValue('country', value);
                  setValue('zip', '');
                }}
                value={watchCountry}
              />
            </View>
          )}
          {!address && !isPanelUnlocked ? null : (
            <View mb={isDesktop ? '40px' : '24px'}>
              <Controller
                name="address"
                control={control}
                render={({field}) => (
                  <FormField
                    nativeID={`${TEST_ID_PREFIX}-business-address-1`}
                    testID={`${TEST_ID_PREFIX}-business-address-1`}
                    label="BUSINESS ADDRESS"
                    editable={isPanelUnlocked}
                    isInvalid={!!errors.address}
                    isInvalidLabel={errors.address?.message}
                    onChangeText={field.onChange}
                    onBlur={field.onBlur}
                    value={field.value}
                  />
                )}
              />
            </View>
          )}
          {!address2 && !isPanelUnlocked ? null : (
            <View mb={isDesktop ? '40px' : '24px'}>
              <Controller
                name="address2"
                control={control}
                render={({field}) => (
                  <FormField
                    nativeID={`${TEST_ID_PREFIX}-business-address-2`}
                    testID={`${TEST_ID_PREFIX}-business-address-2`}
                    label="BUSINESS ADDRESS 2"
                    editable={isPanelUnlocked}
                    isInvalid={!!errors.address2}
                    isInvalidLabel={errors.address2?.message}
                    onChangeText={field.onChange}
                    onBlur={field.onBlur}
                    value={field.value}
                  />
                )}
              />
            </View>
          )}
          {isDesktop ? null : !city && !isPanelUnlocked ? null : (
            <View minWidth={178} flexGrow={1} mb={isDesktop ? '40px' : '24px'}>
              <Controller
                name="city"
                control={control}
                render={({field}) => (
                  <FormField
                    nativeID={`${TEST_ID_PREFIX}-city`}
                    testID={`${TEST_ID_PREFIX}-city`}
                    label={'CITY'}
                    editable={isPanelUnlocked}
                    isInvalid={!!errors?.city}
                    isInvalidLabel={errors?.city?.message}
                    onChangeText={field.onChange}
                    onBlur={field.onBlur}
                    value={field.value}
                  />
                )}
              />
            </View>
          )}
          <Flex flexDirection={'row'}>
            {(!city && !isPanelUnlocked) || !isDesktop ? null : (
              <View minWidth={178} mr={5} flexGrow={1}>
                <Controller
                  name="city"
                  control={control}
                  render={({field}) => (
                    <FormField
                      nativeID={`${TEST_ID_PREFIX}-city`}
                      testID={`${TEST_ID_PREFIX}-city`}
                      label={'CITY'}
                      editable={isPanelUnlocked}
                      isInvalid={!!errors?.city}
                      isInvalidLabel={errors?.city?.message}
                      onChangeText={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                    />
                  )}
                />
              </View>
            )}
            {(!stateprovince && !isPanelUnlocked) ||
            (!isUSA && !isCanada) ? null : (
              <View
                maxWidth={100}
                mr={isDesktop ? 6 : 5}
                flexShrink={1}
                flex={isDesktop ? null : 1}>
                {isUSA ? (
                  <StateDropdown
                    maxHeight={dropdownMaxHeight}
                    required={!!errors.state}
                    editable={isPanelUnlocked}
                    value={stateDisplay ? watchState : undefined}
                    onSelected={value => setValue('state', value)}
                  />
                ) : isCanada ? (
                  <ProvinceDropdown
                    maxHeight={dropdownMaxHeight}
                    required={!!errors.province}
                    editable={isPanelUnlocked}
                    value={provinceDisplay ? watchProvince : undefined}
                    onSelected={value => setValue('province', value)}
                  />
                ) : null}
              </View>
            )}
            {!zip && !isPanelUnlocked ? null : (
              <View maxWidth={140} flexShrink={1} flex={isDesktop ? null : 1}>
                <Controller
                  name="zip"
                  control={control}
                  render={({field}) => (
                    <FormField
                      maxLength={isUSA ? 10 : isCanada ? 7 : undefined}
                      nativeID={`${TEST_ID_PREFIX}-zip-code`}
                      testID={`${TEST_ID_PREFIX}-zip-code`}
                      label={isUSA ? 'ZIP CODE' : 'POSTAL CODE'}
                      editable={isPanelUnlocked}
                      isInvalid={!!errors?.zip}
                      isInvalidLabel={errors?.zip?.message}
                      onChangeText={text => {
                        field.onChange(maskZip(text));
                      }}
                      value={field.value}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </View>
            )}
          </Flex>
        </View>
      </View>
    </PanelContainer>
  );
};

export default BusinessInfoModule;
